import Component from '@glimmer/component';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { initializeVdrClient } from 'scaleflow/utils/aws-clients/s3VDRClient';
import { s3KeyFromLink } from 'scaleflow/utils/s3-key-from-link';
import type StoreService from 'scaleflow/services/store';

interface ScansS3DownloadButtonSignature {
  Args: {
    link: string;
    label?: string;
  };
  Element: null;
}

export default class ScansS3DownloadButton extends Component<ScansS3DownloadButtonSignature> {
  @service declare store: StoreService;

  @tracked downloading = false;
  @tracked progress = 0;

  get label() {
    return this.args.label ?? 'Download';
  }

  get s3Key() {
    return s3KeyFromLink(this.args.link);
  }

  get fileName() {
    return this.s3Key.split('/').pop() ?? 'download';
  }

  @action
  async download() {
    if (this.downloading) return;
    this.downloading = true;
    this.progress = 0;

    try {
      const client = await initializeVdrClient(this.store);
      const downloader = client.downloadFileWithProgress(this.s3Key);
      let downloadedBlob: Blob | null = null;

      for await (const { blob, progress } of downloader) {
        this.progress = progress;
        if (blob) downloadedBlob = blob;
      }

      if (!downloadedBlob) return;

      const fileUrl = URL.createObjectURL(downloadedBlob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = this.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileUrl);
    } finally {
      this.downloading = false;
      this.progress = 0;
    }
  }
}
